import { lerpPose, type Pose } from "pixelfx";
import { FIGHTER, type AbilityDef, type FighterState, type SuperDef } from "../../sim";
import {
  ATTACK_POSES,
  AIR_HIT,
  BLOCK,
  CROUCH,
  CROUCH_BLOCK,
  GETUP,
  HITSTUN,
  JUMP,
  LYING,
  STANCE,
  VICTORY,
} from "./poses";

const easeOut = (u: number): number => 1 - (1 - u) * (1 - u);

/** Поза в серии суперприёма: замах к удару и удар на его кадре; у жертвы — отдача. */
export function superPose(move: SuperDef, cinematicFrame: number, role: "attacker" | "victim"): Pose {
  if (role === "victim") {
    const struck = move.hits.some((hit) => cinematicFrame >= hit.frame && cinematicFrame < hit.frame + 6);
    return struck ? { ...HITSTUN, lean: -9 } : HITSTUN;
  }
  const next = [
    ...move.hits.map((hit) => ({ frame: hit.frame, pose: hit.pose })),
    { frame: move.finale.frame, pose: move.finale.pose },
  ].find((hit) => cinematicFrame <= hit.frame + 6);
  const set = ATTACK_POSES[next?.pose ?? ""];
  if (!next || !set) return STANCE;
  const lead = next.frame - cinematicFrame;
  if (lead > 10) return set.windup;
  if (lead > 0) return lerpPose(set.windup, set.strike, easeOut(1 - lead / 10));
  return set.strike;
}

/** Поза бойца в этот кадр — по его состоянию и кадру способности. */
export function poseFor(fighter: FighterState, ability: AbilityDef | undefined): Pose {
  switch (fighter.state) {
    case FIGHTER.ability: {
      const set = ATTACK_POSES[ability?.pose ?? ""];
      if (!ability || !set) return STANCE;
      const frame = fighter.abilityFrame;
      if (frame < ability.startup) return lerpPose(STANCE, set.windup, easeOut(frame / Math.max(1, ability.startup)));
      if (frame < ability.startup + ability.active) return set.strike;
      const recovery = (frame - ability.startup - ability.active) / Math.max(1, ability.recovery);
      return lerpPose(set.strike, fighter.y < 0 ? JUMP : STANCE, easeOut(recovery));
    }
    case FIGHTER.walkForward:
    case FIGHTER.walkBack: {
      const swing = Math.sin(fighter.stateFrame / 4.5) * 5;
      return { ...STANCE, ffx: 7 + swing, fbx: -7 - swing, ffy: swing > 0 ? -2 : 0, fby: swing < 0 ? -2 : 0 };
    }
    case FIGHTER.crouch:
      return CROUCH;
    case FIGHTER.block:
    case FIGHTER.blockstun:
      return BLOCK;
    case FIGHTER.crouchBlock:
      return CROUCH_BLOCK;
    case FIGHTER.jump:
      return JUMP;
    case FIGHTER.hitstun:
      return HITSTUN;
    case FIGHTER.airHit:
      return AIR_HIT;
    case FIGHTER.getup:
      return GETUP;
    case FIGHTER.victory:
      return VICTORY;
    case FIGHTER.superAttack: {
      const set = ATTACK_POSES.dashStrike;
      if (!set) return STANCE;
      return fighter.stateFrame < 10 ? lerpPose(STANCE, set.windup, easeOut(fighter.stateFrame / 10)) : set.strike;
    }
    case FIGHTER.superVictim:
      return HITSTUN;
    case FIGHTER.knockdown:
    case FIGHTER.ko:
      return fighter.y < 0 ? AIR_HIT : LYING;
    default: {
      const breath = Math.round((Math.sin(fighter.stateFrame / 14) + 1) * 0.6);
      return { ...STANCE, crouch: 3 + breath };
    }
  }
}
